import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { decodeJwtPayload } from './app/shared/helpers/decode-jwt-payload.helper';
import { environment } from './environments/environment';

type WingsMessageData = {
  wingsJsInfo?: string;
};

type BootstrapWindow = Pick<Window, 'history' | 'location'> & {
  onmessage: ((event: MessageEvent<WingsMessageData | undefined>) => void) | null;
  readonly parent?: unknown;
  readonly self?: unknown;
};

const token = '<<some access token here>>';

export const bootstrapApp = (bootstrap = bootstrapApplication) => bootstrap(App, appConfig);

export const logBootstrapError = (error: unknown): void => {
  console.error(error);
};

export const handleWindowMessage = async (
  event: MessageEvent<WingsMessageData | undefined>,
  bootstrap = bootstrapApp,
  logError = logBootstrapError,
): Promise<void> => {
  const wingsJsInfo = event.data?.wingsJsInfo;

  if (wingsJsInfo) {
    localStorage.setItem('encodedAccessToken', wingsJsInfo);
  }

  try {
    await bootstrap();
  } catch (error) {
    logError(error);
  }
};

const isJwtCandidate = (value: string): boolean => value.split('.').length === 3 && value.split('.').every(Boolean);

export const initializeLocalSession = (
  currentWindow: BootstrapWindow = globalThis,
  encodedToken = token,
  decodePayload = decodeJwtPayload,
): void => {
  if (!environment.local) {
    return;
  }

  if (!isJwtCandidate(encodedToken)) {
    return;
  }

  const payload = decodePayload(encodedToken);
  const url = new URL(currentWindow.location.href);
  url.searchParams.set('userInfo', JSON.stringify({ userId: payload['uid'], country: payload['c'], ...payload }));
  url.searchParams.set('user', payload['uid'] as string);
  currentWindow.history.replaceState({}, '', url.toString());

  currentWindow.onmessage?.(new MessageEvent<WingsMessageData>('message', { data: { wingsJsInfo: encodedToken } }));
};

export const isEmbedded = (currentWindow: BootstrapWindow): boolean =>
  currentWindow.parent !== undefined && currentWindow.parent !== currentWindow.self;

export const setupBootstrap = (
  currentWindow: BootstrapWindow = globalThis,
  bootstrap = bootstrapApp,
  logError = logBootstrapError,
): void => {
  let bootstrapped = false;
  const bootstrapOnce: typeof bootstrap = () => {
    if (bootstrapped) return Promise.resolve() as unknown as ReturnType<typeof bootstrap>;
    bootstrapped = true;
    return bootstrap();
  };

  currentWindow.onmessage = (event) => {
    void handleWindowMessage(event, bootstrapOnce, logError);
  };

  try {
    initializeLocalSession(currentWindow);
  } catch (error) {
    logError(error);
  }

  if (!isEmbedded(currentWindow)) {
    bootstrapOnce().catch(logError);
  }
};

setupBootstrap();
